import { ReactNode, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ElementSelector, useDeferredElements } from '@cast/utils';

import { TourStepArrow } from './_arrow/TourStepArrow';

import { Stack } from '@cast/design-system';

type Props = {
  children: ReactNode;
  /**
   * Right now arrows might point only to the objects which are on the left/right from the tooltip
   */
  pointTo: ElementSelector[];
};

export const TourStepContainer = (props: Props) => {
  const leftSlotElementRef = useRef<HTMLElement | null>(null);
  const leftSlotElement = leftSlotElementRef.current;

  const rightSlotElementRef = useRef<HTMLElement | null>(null);
  const rightSlotElement = rightSlotElementRef.current;

  const pointedElements = useDeferredElements(props.pointTo);
  const [leftSlotRect, setLeftSlotRect] = useState<DOMRect | null>(null);

  useLayoutEffect(() => {
    const element = leftSlotElementRef.current;
    if (!element) {
      return;
    }

    const measure = () => {
      const rect = element.getBoundingClientRect();
      setLeftSlotRect((prev) => {
        if (
          prev?.x === rect.x &&
          prev.y === rect.y &&
          prev.width === rect.width &&
          prev.height === rect.height
        ) {
          return prev;
        }

        return rect;
      });
    };

    let frameId = requestAnimationFrame(measure);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => {
            cancelAnimationFrame(frameId);
            frameId = requestAnimationFrame(measure);
          })
        : null;

    resizeObserver?.observe(element);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver?.disconnect();
    };
  }, [pointedElements]);

  const [elementsOnTheLeft, elementsOnTheRight] = useMemo(() => {
    if (!leftSlotRect) {
      return [[], []];
    }

    return [
      pointedElements.filter((it) => {
        const itRect = it.getBoundingClientRect();
        return itRect.x + itRect.width < leftSlotRect.x;
      }),
      pointedElements.filter((it) => {
        const itRect = it.getBoundingClientRect();
        return itRect.x >= leftSlotRect.x;
      }),
    ];
  }, [pointedElements, leftSlotRect]);

  return (
    <Stack
      pt={12}
      pb={16}
      px={24}
      gap={4}
      bgcolor="indigo.700"
      borderRadius="8px"
      position="relative"
      width={360}
      minWidth={360}
      maxWidth={360}
      sx={{
        '@keyframes fadeIn': {
          from: { opacity: 0 },
          to: { opacity: 1 },
        },
        animation: 'fadeIn 0.3s ease-in-out 0.5s backwards',
      }}
      data-testid="demo-tour-step"
    >
      <div style={{ position: 'absolute', left: -4, top: 50 }}>
        <span ref={leftSlotElementRef} />

        {leftSlotElement &&
          elementsOnTheLeft.map((to, index) => (
            <TourStepArrow
              key={index}
              fromElement={leftSlotElement}
              toElement={to}
            />
          ))}
      </div>

      <div style={{ position: 'absolute', right: -4, top: 50 }}>
        <span ref={rightSlotElementRef} />

        {rightSlotElement &&
          elementsOnTheRight.map((to, index) => (
            <TourStepArrow
              key={index}
              fromElement={rightSlotElement}
              toElement={to}
            />
          ))}
      </div>

      {props.children}
    </Stack>
  );
};
